import {
  Autocomplete,
  FormControl,
  FormLabel,
  TextField,
  type AutocompleteRenderInputParams,
} from '@mui/material';
import { useEffect } from 'react';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

const title = 'Subnet';

export const AWSSubnetSelect = ({
  accountId,
  regionName,
  subnetId,
  onChange,
  hideLabel,
  availabilityZoneScope,
  vpcScope,
}: {
  accountId?: string;
  regionName?: string;
  subnetId?: string;
  subnetName?: string;
  availabilityZoneScope?: string;
  vpcScope: string;
  onChange: (subnetId?: string, isUserChange?: boolean) => void;
  hideLabel?: boolean;
}) => {
  if (!accountId || !regionName || !vpcScope) {
    return <EmptySelect title={hideLabel ? '' : title} />;
  }

  return (
    <InnerAWSSubnetSelect
      accountId={accountId}
      regionName={regionName}
      subnetId={subnetId}
      onChange={onChange}
      hideLabel={hideLabel}
      availabilityZoneScope={availabilityZoneScope}
      vpcScope={vpcScope}
    />
  );
};

const InnerAWSSubnetSelect = ({
  accountId,
  regionName,
  subnetId,
  onChange,
  hideLabel,
  availabilityZoneScope,
  vpcScope,
}: {
  accountId: string;
  regionName: string;
  subnetId?: string;
  availabilityZoneScope?: string;
  vpcScope?: string;
  onChange: (subnetId?: string, event?: boolean) => void;
  hideLabel?: boolean;
}) => {
  const dal = useDAL();
  const { data: body, isLoading } = dal.restore.awsSubnets.list(
    accountId,
    regionName
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (subnetId && !body?.subnets?.some((x) => x.id === subnetId)) {
      onChange(undefined);
    }
  }, [body?.subnets, isLoading, onChange, subnetId]);

  if (!body?.subnets?.length) {
    return <EmptySelect title={hideLabel ? '' : title} />;
  }

  const availableSubnets = body.subnets
    .filter(
      (x) =>
        !availabilityZoneScope || x.availabilityZone === availabilityZoneScope
    )
    .filter((x) => !vpcScope || x.vpc === vpcScope);

  const selectedSubnet = availableSubnets.find((x) => x.id === subnetId);

  return (
    <FormControl size='small' className='flex-1'>
      {!hideLabel && <FormLabel>{title}</FormLabel>}
      <Autocomplete
        clearOnBlur
        data-testid='awsSubnetSelect'
        size='small'
        value={selectedSubnet || null}
        onChange={(event, newValue) =>
          onChange(newValue?.id || undefined, !!event)
        }
        getOptionLabel={(option) => option?.id || ''}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderInput={(params: AutocompleteRenderInputParams) => {
          return (
            <TextField
              {...params}
              sx={{
                '& input::placeholder': {
                  color: 'var(--mui-palette-text-primary)',
                  opacity: 1,
                },
              }}
              placeholder='Select'
              label=''
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
        options={availableSubnets}
      />
    </FormControl>
  );
};
